import type { PrimaryFilterId } from '../../filters/types/filters';
import type { LevelDimension, TableDimension } from '../DataProvider/types';
import {
  type CompositionV4Metric,
  isCompositionV4Metric,
  type MetricValues,
} from '../PageConfigProvider/types/metric';
import {
  type CompositionV4BreakdownColumn,
  isCompositionV4BreakdownDimension,
  isCompositionV4BreakdownTreeDimension,
  isCompositionV4LeveledDimension,
} from './composition-v4-breakdowns';
import { dimensionFromPrimaryFilter } from './convert-composition-v4-data';
import {
  isLevelDimension,
  isSublevelOf,
  taxonomyOfLevel,
} from './taxonomy-levels';

export const MAX_COMPOSITION_V4_BREAKDOWNS = 8;

const BREAKDOWN_METRICS: readonly MetricValues[] = [
  'headcount',
  'share_of_headcount',
];

type CompositionV4TableSpec = {
  dimensions: readonly TableDimension[];
  metrics: readonly MetricValues[];
  topN?: number;
};

export type CompositionV4TableSource =
  | { kind: 'metrics'; timeseries: boolean; metrics: CompositionV4Metric[] }
  | {
      kind: 'breakdown';
      timeseries: boolean;
      dimensions:
        | readonly [CompositionV4BreakdownColumn]
        | readonly [LevelDimension, LevelDimension];
      topN?: number;
    };

type BreakdownDimensions = Extract<
  CompositionV4TableSource,
  { kind: 'breakdown' }
>['dimensions'];

const getBreakdownDimensions = ([
  first,
  second,
  ...more
]: readonly TableDimension[]): BreakdownDimensions | undefined => {
  if (first === undefined || more.length > 0) return undefined;
  if (second === undefined) {
    if (isLevelDimension(first)) return [first];
    return isCompositionV4BreakdownDimension(first) &&
      !isCompositionV4LeveledDimension(first)
      ? [first]
      : undefined;
  }
  const [level, sublevel] = isSublevelOf(first, second)
    ? [second, first]
    : [first, second];
  return isLevelDimension(level) &&
    isLevelDimension(sublevel) &&
    isSublevelOf(sublevel, level) &&
    isCompositionV4BreakdownTreeDimension(taxonomyOfLevel(level))
    ? [level, sublevel]
    : undefined;
};

export const getCompositionV4TableSource = (
  primaryDimension: PrimaryFilterId,
  { dimensions, metrics, topN }: CompositionV4TableSpec
): CompositionV4TableSource | undefined => {
  const entity = dimensionFromPrimaryFilter(primaryDimension);
  if (!dimensions.includes(entity) || metrics.length === 0) return undefined;
  const timeseries = dimensions.includes('timeseries');
  const breakdowns = dimensions.filter(
    (dimension) => dimension !== entity && dimension !== 'timeseries'
  );

  if (breakdowns.length === 0) {
    return metrics.every(isCompositionV4Metric)
      ? { kind: 'metrics', timeseries, metrics: [...metrics] }
      : undefined;
  }

  if (!metrics.every((metric) => BREAKDOWN_METRICS.includes(metric))) {
    return undefined;
  }
  const breakdownDimensions = getBreakdownDimensions(breakdowns);
  if (!breakdownDimensions) return undefined;
  return timeseries && breakdownDimensions.length > 1
    ? undefined
    : {
        kind: 'breakdown',
        timeseries,
        dimensions: breakdownDimensions,
        ...(topN !== undefined && { topN }),
      };
};

export const getCompositionV4BreakdownNames = (
  source: CompositionV4TableSource
): readonly string[] => (source.kind === 'breakdown' ? source.dimensions : []);

export const getCompositionV4TableSources = (
  primaryDimension: PrimaryFilterId,
  tables: Readonly<Record<string, CompositionV4TableSpec>>
): Readonly<Record<string, CompositionV4TableSource>> =>
  Object.keys(tables).reduce<Record<string, CompositionV4TableSource>>(
    (sources, key) => {
      const table = tables[key];
      const source =
        table && getCompositionV4TableSource(primaryDimension, table);
      if (source) sources[key] = source;
      return sources;
    },
    {}
  );

export const getCompositionV4Metrics = (
  sources: Readonly<Record<string, CompositionV4TableSource>>
): CompositionV4Metric[] => {
  const metrics = new Set<CompositionV4Metric>();
  for (const source of Object.values(sources)) {
    if (source.kind === 'metrics') {
      for (const metric of source.metrics) metrics.add(metric);
    }
  }
  return Array.from(metrics).sort();
};
